import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Recipe Modal Component
const RecipeModal = ({ isOpen, onClose, recipe }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-900">{recipe?.name || 'Recipe'}</h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          
          {recipe?.video_id && (
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800 mb-2">Source:</h4>
              <a 
                href={`https://youtube.com/watch?v=${recipe.video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
                Watch on YouTube
              </a>
            </div>
          )}
          
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Nutritional Info:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm bg-gray-50 p-3 rounded-lg">
              <div className="text-center">
                <div className="font-medium">{recipe?.calories || 0}</div>
                <div className="text-xs text-gray-500">Calories</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{recipe?.protein || 0}g</div>
                <div className="text-xs text-gray-500">Protein</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{recipe?.carbs || 0}g</div>
                <div className="text-xs text-gray-500">Carbs</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{recipe?.fat || 0}g</div>
                <div className="text-xs text-gray-500">Fat</div>
              </div>
            </div>
          </div>

          {recipe?.recipe ? (
            <>
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">Ingredients:</h4>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {Array.isArray(recipe.recipe.ingredients) 
                    ? recipe.recipe.ingredients.map((ingredient, idx) => (
                        <li key={idx} className="text-sm">{ingredient}</li>
                      ))
                    : <li className="text-sm">No ingredients available</li>
                  }
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Instructions:</h4>
                <div className="prose max-w-none text-gray-700 text-sm whitespace-pre-line">
                  {recipe.recipe.recipe}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-gray-500">
              Recipe details not available for this item.
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MealPlannerPage = () => {
  const { user, guestMode } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('next_meal');
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMeals, setSelectedMeals] = useState({});
  const scrollPositionRef = useRef({ x: 0, y: 0 });
  const isScrollingRef = useRef(false);
  
  // Toggle meal selection
  const toggleMealSelection = (mealId, mealType, dayIndex = null) => {
    setSelectedMeals(prev => {
      const key = dayIndex !== null ? `${dayIndex}-${mealType}-${mealId}` : `${mealType}-${mealId}`;
      const newSelection = { ...prev };
      if (newSelection[key]) {
        delete newSelection[key];
      } else {
        newSelection[key] = { mealId, mealType, dayIndex };
      }
      return newSelection;
    });
  };
  
  // Check if a meal is selected
  const isMealSelected = (mealId, mealType, dayIndex = null) => {
    const key = dayIndex !== null ? `${dayIndex}-${mealType}-${mealId}` : `${mealType}-${mealId}`;
    return !!selectedMeals[key];
  };
  
  // Get selected meals count
  const getSelectedMealsCount = () => {
    return Object.keys(selectedMeals).length;
  };
  
  // Clear all selections
  const clearSelections = () => {
    setSelectedMeals({});
  };
  
  // Handle scroll position before updates
  useEffect(() => {
    if (!isScrollingRef.current) {
      scrollPositionRef.current = {
        x: window.scrollX,
        y: window.scrollY
      };
    }
  });

  // Restore scroll position after updates
  useEffect(() => {
    if (isScrollingRef.current) {
      const { x, y } = scrollPositionRef.current;
      window.scrollTo(x, y);
      isScrollingRef.current = false;
    }
  });

  // Regenerate a specific meal
  const regenerateMeal = async (mealId, mealType, dayIndex = null) => {
    // Save current scroll position
    scrollPositionRef.current = {
      x: window.scrollX,
      y: window.scrollY
    };
    isScrollingRef.current = true;
    
    try {
      await fetchSuggestions(activeTab, { mealId, mealType, dayIndex });
    } finally {
      isScrollingRef.current = true; // Ensure scroll is restored after state updates
    }
  };
  
  // Filter states
  const [filterRegion, setFilterRegion] = useState('');
  const [filterFoodType, setFilterFoodType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [mealTypeOverride, setMealTypeOverride] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [nextMealTime, setNextMealTime] = useState('');
  const [editMode, setEditMode] = useState({ isEditing: false, mealId: null, mealType: null });
  const [mealOptions, setMealOptions] = useState({});
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Function to determine the next meal time
  const getNextMealTime = () => {
    const now = new Date();
    const hours = now.getHours();
    
    if (hours < 10) return 'breakfast';
    if (hours < 15) return 'lunch';
    if (hours < 21) return 'dinner';
    // If it's late at night, suggest breakfast for tomorrow
    setSelectedDate(prevDate => {
      const tomorrow = new Date(prevDate);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    });
    return 'breakfast';
  };

  // Fetch all food items from the database
  const fetchAllFoodItems = async (searchQuery = '') => {
    try {
      const response = await axios.get(`${API}/foods/database`, {
        params: {
          search: searchQuery,
          limit: 100 // Increase limit to get more items
        }
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching food items:', error);
      return [];
    }
  };

  // Fetch meal options - filtered by meal type
  const fetchMealOptions = async (mealType, searchQuery = '', isEditMode = false) => {
    if (isLoadingOptions) return [];
    
    try {
      setIsLoadingOptions(true);
      
      let options = [];
      
      if (isEditMode) {
        // In edit mode, fetch food items but still filter by meal type
        const response = await axios.get(`${API}/foods/database`, {
          params: {
            search: searchQuery,
            meal_type: mealType,  // Filter by the specific meal type
            limit: 100
          }
        });
        options = response.data || [];
      } else {
        // In normal mode, use the suggest endpoint with meal type filtering
        const response = await axios.post(`${API}/meal-plans/suggest`, {
          suggestion_type: 'next_meal',
          meal_type_override: mealType,
          filter_meal_type: mealType,
          search: searchQuery,
          limit: 50
        });
        options = response.data?.suggestions || [];
      }
      
      // Cache the results
      setMealOptions(prev => ({
        ...prev,
        [mealType]: options
      }));
      
      return options;
    } catch (error) {
      console.error('Error fetching meal options:', error);
      toast.error('Failed to load meal options. Please try again.');
      return [];
    } finally {
      setIsLoadingOptions(false);
    }
  };
  
    // Custom dropdown component with search
  const SearchableDropdown = ({ 
    value, 
    onChange, 
    options = [], 
    isLoading = false,
    onInputChange,
    placeholder = 'Search meals...',
  }) => {
    const customStyles = {
      control: (provided) => ({
        ...provided,
        minHeight: '40px',
        borderRadius: '0.5rem',
        borderColor: 'rgba(24, 24, 24, 0.2)',
        '&:hover': {
          borderColor: 'rgba(124, 58, 237, 0.8)'
        },
        boxShadow: 'none',
        '&:focus-within': {
          borderColor: 'rgba(124, 58, 237, 0.8)',
          boxShadow: '0 0 0 1px rgba(124, 58, 237, 0.25)'
        }
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? 'rgba(124, 58, 237, 0.1)' : 'white',
        color: state.isSelected ? 'rgb(24, 24, 24)' : 'rgb(24, 24, 24)',
        '&:hover': {
          backgroundColor: 'rgba(124, 58, 237, 0.1)'
        },
        padding: '8px 12px',
        fontSize: '0.875rem',
        cursor: 'pointer'
      }),
      menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '0.5rem',
        border: '1px solid rgba(24, 24, 24, 0.1)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }),
      input: (provided) => ({
        ...provided,
        color: 'rgb(24, 24, 24)',
        fontSize: '0.875rem',
        padding: '4px 0'
      }),
      placeholder: (provided) => ({
        ...provided,
        color: 'rgb(112, 112, 112)',
        fontSize: '0.875rem'
      }),
      singleValue: (provided) => ({
        ...provided,
        color: 'rgb(24, 24, 24)',
        fontSize: '0.875rem'
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        color: 'rgb(112, 112, 112)',
        padding: '0 8px',
        '&:hover': {
          color: 'rgb(24, 24, 24)'
        }
      })
    };

    return (
      <Select
        value={value}
        onChange={onChange}
        options={options}
        isLoading={isLoading}
        onInputChange={onInputChange}
        placeholder={isLoading ? 'Loading...' : placeholder}
        isSearchable
        styles={customStyles}
        className="w-full text-sm"
        classNamePrefix="select"
        noOptionsMessage={({ inputValue }) => 
          inputValue ? 'No meals found' : 'Type to search meals...'
        }
        loadingMessage={() => 'Loading meals...'}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    );
  };

  // Handle meal selection change
  const handleMealChange = async (mealType, currentMealId, newMealId) => {
    if (!suggestions?.meal_plan?.[mealType]) return;
    
    try {
      // Find the new meal from options
      const newMeal = mealOptions[mealType]?.find(m => m.id === newMealId);
      if (!newMeal) return;
      
      // Update the meal in the current plan
      setSuggestions(prev => {
        if (!prev?.meal_plan?.[mealType]) return prev;
        
        const updatedMeals = prev.meal_plan[mealType].map(meal => 
          meal.id === currentMealId ? { ...newMeal } : meal
        );
        
        return {
          ...prev,
          meal_plan: {
            ...prev.meal_plan,
            [mealType]: updatedMeals
          },
          totals: calculateTotals({
            ...prev.meal_plan,
            [mealType]: updatedMeals
          })
        };
      });
      
      // Exit edit mode
      setEditMode({ isEditing: false, mealId: null, mealType: null });
      toast.success('Meal updated successfully!');
      
    } catch (error) {
      console.error('Error updating meal:', error);
      toast.error('Failed to update meal');
    }
  };

  // Toggle edit mode for a meal
  const toggleEditMode = (mealId, mealType) => {
    const newEditMode = {
      isEditing: !(editMode.isEditing && editMode.mealId === mealId && editMode.mealType === mealType),
      mealId,
      mealType
    };
    
    setEditMode(newEditMode);

    if (newEditMode.isEditing) {
      // In edit mode, fetch all food items
      fetchMealOptions(mealType, '', true);
    } else if (!mealOptions[mealType] || mealOptions[mealType].length === 0) {
      // When exiting edit mode, fetch filtered options if not already loaded
      fetchMealOptions(mealType);
    }
  };
  

  const fetchSuggestions = async (type, regenerateMeal = null) => {
    setLoading(true);
    try {
      // For next_meal, determine the appropriate meal time and date
      const mealTime = type === 'next_meal' ? getNextMealTime() : mealTypeOverride;
      const useDate = type === 'next_meal' ? new Date() : selectedDate;
      
      // Set the next meal time for display
      if (type === 'next_meal') {
        setNextMealTime(mealTime);
      }
      
      const payload = {
        suggestion_type: type,
        filter_region: filterRegion || null,
        filter_food_type: filterFoodType || null,
        filter_category: filterCategory || null,
        meal_type_override: mealTime || null,
        date: useDate.toISOString().split('T')[0],
        regenerate_meal: regenerateMeal
      };
      
      const response = await axios.post(`${API}/meal-plans/suggest`, payload);
      
      if (regenerateMeal) {
        // If regenerating a specific meal, update just that meal in the current suggestions
        setSuggestions(prev => {
          if (!prev) return response.data;
          
          if (type === 'weekly' && prev.weekly_plan) {
            const updatedPlan = [...prev.weekly_plan];
            if (updatedPlan[regenerateMeal.dayIndex]?.meal_plan?.[regenerateMeal.mealType]) {
              updatedPlan[regenerateMeal.dayIndex].meal_plan[regenerateMeal.mealType] = 
                response.data.weekly_plan[0].meal_plan[regenerateMeal.mealType];
              
              // Update totals
              updatedPlan[regenerateMeal.dayIndex].totals = 
                calculateDayTotals(updatedPlan[regenerateMeal.dayIndex].meal_plan);
                
              return { ...prev, weekly_plan: updatedPlan };
            }
          } else if (type === 'daily' && prev.meal_plan) {
            const updatedMealPlan = { ...prev.meal_plan };
            updatedMealPlan[regenerateMeal.mealType] = response.data.meal_plan[regenerateMeal.mealType];
            
            return { 
              ...prev, 
              meal_plan: updatedMealPlan,
              totals: calculateTotals(updatedMealPlan)
            };
          } else if (type === 'next_meal' && prev.suggestions) {
            // For next_meal, replace the specific suggestion with the regenerated one
            const updatedSuggestions = prev.suggestions.map(meal =>
              meal.id === regenerateMeal.mealId ? response.data.suggestions[0] : meal
            );

            return {
              ...prev,
              suggestions: updatedSuggestions
            };
          }
          
          return prev;
        });
      } else {
        // If generating a new plan, update all suggestions and clear selections
        setSuggestions(response.data);
        clearSelections();
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      alert('Failed to get suggestions: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to calculate day totals
  const calculateDayTotals = (meals) => {
    return Object.values(meals).flat().reduce((totals, meal) => ({
      calories: (totals.calories || 0) + (meal.calories || 0),
      protein: (totals.protein || 0) + (meal.protein || 0),
      carbs: (totals.carbs || 0) + (meal.carbs || 0),
      fat: (totals.fat || 0) + (meal.fat || 0)
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };
  
  // Helper function to calculate totals
  const calculateTotals = (meals) => {
    return Object.values(meals).flat().reduce((totals, meal) => ({
      calories: (totals.calories || 0) + (meal.calories || 0),
      protein: (totals.protein || 0) + (meal.protein || 0),
      carbs: (totals.carbs || 0) + (meal.carbs || 0),
      fat: (totals.fat || 0) + (meal.fat || 0)
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  useEffect(() => {
    fetchSuggestions(activeTab);
  }, [activeTab]);

  const handleViewRecipe = (meal) => {
    // Fetch recipe details from the backend
    setSelectedRecipe(meal);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecipe(null);
  };

  // Helper function to safely extract meal data
  const extractMealData = (meal) => {
    if (!meal || typeof meal !== 'object') return null;
    
    // Create a new object with only the properties we need
    const safeMeal = {
      id: meal.id || null,
      name: meal.name || '',
      calories: Number(meal.calories) || 0,
      protein: Number(meal.protein) || 0,
      carbs: Number(meal.carbs) || 0,
      fat: Number(meal.fat) || 0,
      meal_type: meal.meal_type || '',
      category: meal.category || '',
      type: meal.type || '',
      region: meal.region || '',
      ingredients: Array.isArray(meal.ingredients) ? [...meal.ingredients] : [],
      recipe: typeof meal.recipe === 'string' ? meal.recipe : 
              (meal.recipe ? JSON.stringify(meal.recipe) : ''),
      video_id: meal.video_id || null,
      date: meal.date || null
    };
    
    return safeMeal;
  };

  const savePlan = async (planName) => {
    // Check if user is in guest mode
    if (guestMode) {
      toast.info('Please sign in to save meal plans');
      return;
    }

    // Format the selected date
    const formattedDate = selectedDate.toISOString().split('T')[0];
    
    // Ensure planName is a string and has a default value
    const planNameStr = (() => {
      if (typeof planName === 'function') {
        return `Meal Plan - ${formattedDate}`;
      }
      if (typeof planName === 'string' && planName.trim() !== '') {
        return planName.trim();
      }
      return `Meal Plan - ${formattedDate}`;
    })();
    if (!suggestions) {
      toast.error('No suggestions available to save');
      return;
    }

    try {
      setLoading(true);
      
      // Initialize variables to store meals and totals
      let meals = {};
      let totalCal = 0;
      let totalProt = 0;
      let totalCarbs = 0;
      let totalFat = 0;
      const planType = suggestions.suggestion_type || 'daily';
      const formattedDate = selectedDate.toISOString().split('T')[0];
      
      // Handle different response formats
      if (planType === 'weekly' && suggestions.weekly_plan) {
        // For weekly plans, flatten all meals from all days
        const allMeals = [];
        suggestions.weekly_plan.forEach(day => {
          if (day && day.meal_plan && typeof day.meal_plan === 'object') {
            Object.entries(day.meal_plan).forEach(([mealType, mealItems]) => {
              if (Array.isArray(mealItems)) {
                mealItems.forEach(meal => {
                  const safeMeal = extractMealData(meal);
                  if (safeMeal) {
                    safeMeal.meal_type = safeMeal.meal_type || mealType;
                    safeMeal.date = day.date || null;
                    allMeals.push(safeMeal);
                  }
                });
              }
            });
          }
        });
        
        // Calculate totals from all meals
        totalCal = allMeals.reduce((sum, m) => sum + (m.calories || 0), 0);
        totalProt = allMeals.reduce((sum, m) => sum + (m.protein || 0), 0);
        totalCarbs = allMeals.reduce((sum, m) => sum + (m.carbs || 0), 0);
        totalFat = allMeals.reduce((sum, m) => sum + (m.fat || 0), 0);
        
        // Use all meals for the save payload
        meals = allMeals;
      } 
      else if (planType === 'daily' && suggestions.meal_plan && typeof suggestions.meal_plan === 'object') {
        // For daily plans, use the meal_plan structure and filter by selection
        Object.entries(suggestions.meal_plan).forEach(([mealType, mealItems]) => {
          if (Array.isArray(mealItems)) {
            // Filter meals to only include selected ones
            const selectedMealIds = Object.values(selectedMeals)
              .filter(m => m.mealType === mealType)
              .map(m => m.mealId);

            const filteredMeals = selectedMealIds.length > 0
              ? mealItems.filter(meal => selectedMealIds.includes(meal.id))
              : mealItems;

            meals[mealType] = filteredMeals
              .map(meal => {
                const safeMeal = extractMealData(meal);
                if (safeMeal) {
                  safeMeal.meal_type = safeMeal.meal_type || mealType;
                  return safeMeal;
                }
                return null;
              })
              .filter(Boolean);
          }
        });
        
        // Calculate totals from filtered meals instead of using provided totals
        Object.values(meals).flat().forEach(meal => {
          if (meal) {
            totalCal += Number(meal.calories) || 0;
            totalProt += Number(meal.protein) || 0;
            totalCarbs += Number(meal.carbs) || 0;
            totalFat += Number(meal.fat) || 0;
          }
        });
      }
      else if (planType === 'next_meal' && suggestions.suggestions) {
        // For next meal suggestions - only save selected meals
        let allSuggestions = Array.isArray(suggestions.suggestions)
          ? suggestions.suggestions
          : [suggestions.suggestions];

        // Filter to only include selected meals
        const selectedMealIds = Object.values(selectedMeals)
          .filter(m => m.mealType === 'suggestion')
          .map(m => m.mealId);

        // If no selections, use all; otherwise only use selected ones
        const filteredSuggestions = selectedMealIds.length > 0
          ? allSuggestions.filter(meal => selectedMealIds.includes(meal.id))
          : allSuggestions;

        const mealItems = filteredSuggestions
          .map(extractMealData)
          .filter(Boolean);

        const mealType = suggestions.meal_type || 'lunch';
        meals[mealType] = mealItems;
        
        // Calculate totals
        totalCal = mealItems.reduce((sum, m) => sum + (m.calories || 0), 0);
        totalProt = mealItems.reduce((sum, m) => sum + (m.protein || 0), 0);
        totalCarbs = mealItems.reduce((sum, m) => sum + (m.carbs || 0), 0);
        totalFat = mealItems.reduce((sum, m) => sum + (m.fat || 0), 0);
      } else {
        // Fallback: Try to extract meals from the root level if they exist
        const possibleMealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
        possibleMealTypes.forEach(mealType => {
          if (Array.isArray(suggestions[mealType])) {
            meals[mealType] = suggestions[mealType]
              .map(extractMealData)
              .filter(Boolean);
          }
        });
        
        if (Object.keys(meals).length === 0) {
          throw new Error('No valid meal data found to save');
        }
      }
      
      // Convert meals object to a list format
      const mealsList = [];
      
      // Flatten the meals object into a list
      Object.entries(meals).forEach(([mealType, mealItems]) => {
        if (Array.isArray(mealItems)) {
          mealItems.forEach(meal => {
            if (meal && typeof meal === 'object') {
              mealsList.push({
                ...meal,
                meal_type: meal.meal_type || mealType
              });
            }
          });
        } else if (mealItems && typeof mealItems === 'object') {
          // Handle case where mealItems is a single meal object
          mealsList.push({
            ...mealItems,
            meal_type: mealItems.meal_type || mealType
          });
        }
      });
      
      if (mealsList.length === 0) {
        throw new Error('No valid meals to save. Please make sure you have meals in your plan.');
      }
      
      // Prepare the save payload with proper date handling
      const savePayload = {
        name: planNameStr.substring(0, 100), // Ensure name is not too long
        type: planType,
        meals: mealsList,
        total_calories: Number(totalCal) || 0,
        total_protein: Number(totalProt) || 0,
        total_carbs: Number(totalCarbs) || 0,
        total_fat: Number(totalFat) || 0
      };

      // Handle date based on plan type
      if (planType === 'weekly') {
        // For weekly plans, use the date range from suggestions or calculate from selected date
        if (suggestions.start_date && suggestions.end_date) {
          savePayload.start_date = suggestions.start_date;
          savePayload.end_date = suggestions.end_date;
        } else {
          // If no date range is provided, calculate a week from the selected date
          const start = new Date(selectedDate);
          const end = new Date(start);
          end.setDate(start.getDate() + 6);
          
          savePayload.start_date = start.toISOString().split('T')[0];
          savePayload.end_date = end.toISOString().split('T')[0];
        }
      } else {
        // For daily and next_meal plans, use the selected date
        savePayload.date = formattedDate;
        savePayload.plan_date = formattedDate; // Ensure plan_date is set to the formatted date
      }
      
      // Send to backend
      const response = await axios.post(`${API}/meal-plans/save`, savePayload);
      
      toast.success('✅ Meal plan saved successfully!');
      return response.data;
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error(`Failed to save plan: ${error.response?.data?.detail || error.message}`);
      throw error;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, rgba(243, 234, 255, 0.8) 0%, rgba(255, 232, 240, 0.8) 25%, rgba(232, 244, 255, 0.8) 50%, rgba(255, 232, 213, 0.8) 75%, rgba(243, 234, 255, 0.8) 100%)' }}>
      
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h4 className="heading-4" style={{ color: 'rgb(24, 24, 24)' }}>🤖 AI Meal Planner</h4>
          <p className="body-medium mb-6" style={{ color: 'rgb(112, 112, 112)' }}>Get personalized meal suggestions based on your goals and preferences</p>
          
          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b" style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}>
            <button
              onClick={() => setActiveTab('next_meal')}
              className={`px-4 py-2 body-medium ${activeTab === 'next_meal' ? 'border-b-2 border-black' : ''}`}
              style={{ color: activeTab === 'next_meal' ? 'rgb(24, 24, 24)' : 'rgb(112, 112, 112)' }}
            >
              Next Meal
            </button>
            <button
              onClick={() => setActiveTab('daily')}
              className={`px-4 py-2 body-medium ${activeTab === 'daily' ? 'border-b-2 border-black' : ''}`}
              style={{ color: activeTab === 'daily' ? 'rgb(24, 24, 24)' : 'rgb(112, 112, 112)' }}
            >
              Daily Plan
            </button>
            <button
              onClick={() => setActiveTab('weekly')}
              className={`px-4 py-2 body-medium ${activeTab === 'weekly' ? 'border-b-2 border-black' : ''}`}
              style={{ color: activeTab === 'weekly' ? 'rgb(24, 24, 24)' : 'rgb(112, 112, 112)' }}
            >
              Weekly Plan
            </button>
          </div>

          {/* Filter Section */}
          <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.2)', borderColor: 'rgba(24, 24, 24, 0.1)' }}>
            <h3 className="body-medium font-semibold mb-3" style={{ color: 'rgb(24, 24, 24)' }}>🎯 Preferences & Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="body-small block mb-1" style={{ color: 'rgb(24, 24, 24)' }}>Region</label>
                <select
                  value={filterRegion}
                  onChange={(e) => setFilterRegion(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                >
                  <option value="">All Regions</option>
                  <option value="North">🔴 North Indian</option>
                  <option value="South">🟢 South Indian</option>
                  <option value="East">🟡 East Indian</option>
                  <option value="West">🟠 West Indian</option>
                </select>
              </div>
              
              <div>
                <label className="body-small block mb-1" style={{ color: 'rgb(24, 24, 24)' }}>Food Type</label>
                <select
                  value={filterFoodType}
                  onChange={(e) => setFilterFoodType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                >
                  <option value="">All Types</option>
                  <option value="veg">🥬 Vegetarian</option>
                  <option value="non-veg">🍗 Non-Veg</option>
                  <option value="vegan">🌱 Vegan</option>
                </select>
              </div>
              
              <div>
                <label className="body-small block mb-1" style={{ color: 'rgb(24, 24, 24)' }}>Category</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                >
                  <option value="">All Categories</option>
                  <option value="bread">🫓 Bread</option>
                  <option value="dal">🍲 Dal</option>
                  <option value="rice">🍚 Rice</option>
                  <option value="main_course">🍛 Main Course</option>
                  <option value="snack">🥙 Snack</option>
                  <option value="dessert">🍰 Dessert</option>
                </select>
              </div>
              
              {activeTab === 'next_meal' && (
                <div>
                  <label className="body-small block mb-1" style={{ color: 'rgb(24, 24, 24)' }}>Meal Time</label>
                  <select
                    value={mealTypeOverride}
                    onChange={(e) => setMealTypeOverride(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                  >
                    <option value="">Auto Detect</option>
                    <option value="breakfast">🌅 Breakfast</option>
                    <option value="lunch">🌞 Lunch</option>
                    <option value="dinner">🌙 Dinner</option>
                    <option value="snack">🍪 Snack</option>
                  </select>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-3">
              <button 
                onClick={() => fetchSuggestions(activeTab)}
                className="btn-primary text-sm px-4 py-2"
              >
                🔍 Apply Filters
              </button>
              <button 
                onClick={() => {
                  setFilterRegion('');
                  setFilterFoodType('');
                  setFilterCategory('');
                  setMealTypeOverride('');
                }}
                className="btn-secondary text-sm px-4 py-2"
              >
                🔄 Clear All
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
              <p className="body-large" style={{ color: 'rgb(112, 112, 112)' }}>Generating suggestions...</p>
            </div>
          ) : suggestions ? (
            <div>
              {/* Next Meal Suggestions */}
              {activeTab === 'next_meal' && suggestions.suggestions && (
                <div>
                  <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
                    <h3 className="heading-4 mb-1" style={{ color: 'rgb(24, 24, 24)' }}>
                      Recommended for {nextMealTime ? nextMealTime.charAt(0).toUpperCase() + nextMealTime.slice(1) : 'your next meal'}
                    </h3>
                    <p className="body-small" style={{ color: 'rgb(112, 112, 112)' }}>
                      {nextMealTime === 'breakfast' ? 'Good morning! ' : 
                       nextMealTime === 'lunch' ? 'Good afternoon! ' : 
                       nextMealTime === 'dinner' ? 'Good evening! ' : ''}
                      {suggestions.remaining_calories > 0 ? (
                        `Remaining today: ${Math.round(suggestions.remaining_calories)} cal | ${Math.round(suggestions.remaining_protein)}g protein`
                      ) : (
                        'All meals planned for today!'
                      )}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {suggestions.suggestions.map((meal, idx) => (
                      <div 
                        key={idx} 
                        className="p-4 rounded-lg border hover:shadow-md transition-all relative group" 
                        style={{ 
                          borderColor: isMealSelected(meal.id, 'suggestion') ? 'rgba(124, 58, 237, 0.5)' : 'rgba(24, 24, 24, 0.1)',
                          backgroundColor: isMealSelected(meal.id, 'suggestion') ? 'rgba(124, 58, 237, 0.05)' : 'white',
                          paddingRight: '2.5rem'
                        }}
                        onClick={() => toggleMealSelection(meal.id, 'suggestion')}
                      >
                        {/* Regenerate button - Top left */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            regenerateMeal(meal.id, 'suggestion');
                          }}
                          className="absolute top-2 left-2 text-gray-400 hover:text-purple-600 p-1"
                          title="Regenerate this meal"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>

                        {/* Checkbox - Top right */}
                        <div className="absolute top-2 right-2 z-10">
                          <input
                            type="checkbox"
                            checked={isMealSelected(meal.id, 'suggestion')}
                            onChange={() => toggleMealSelection(meal.id, 'suggestion')}
                            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="ml-6">
                          <h4 className="body-medium font-semibold mb-2" style={{ color: 'rgb(24, 24, 24)' }}>{meal.name}</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                          <div><span style={{ color: 'rgb(112, 112, 112)' }}>Calories:</span> <strong>{meal.calories}</strong></div>
                          <div><span style={{ color: 'rgb(112, 112, 112)' }}>Protein:</span> <strong>{meal.protein}g</strong></div>
                          <div><span style={{ color: 'rgb(112, 112, 112)' }}>Carbs:</span> <strong>{meal.carbs}g</strong></div>
                          <div><span style={{ color: 'rgb(112, 112, 112)' }}>Fat:</span> <strong>{meal.fat}g</strong></div>
                        </div>
                        <button 
                          onClick={() => handleViewRecipe(meal)}
                          className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                        >
                          👩‍🍳 View Recipe
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {guestMode && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                      <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800">Sign in required to save meal plans</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          <button 
                            onClick={() => navigate('/login')}
                            className="underline hover:text-yellow-900 font-medium"
                          >
                            Create an account or sign in
                          </button> to save your meal plans and track your progress.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2 items-center">
                    <button 
                      onClick={guestMode ? () => navigate('/login') : savePlan} 
                      className="btn-primary"
                      disabled={!guestMode && getSelectedMealsCount() === 0}
                      title={guestMode ? "Sign in to save" : ""}
                    >
                      {guestMode ? '🔒 Sign In to Save Plan' : `💾 Save Plan ${getSelectedMealsCount() > 0 ? `(${getSelectedMealsCount()} selected)` : ''}`}
                    </button>
                    <button 
                      onClick={() => fetchSuggestions('next_meal')} 
                      className="btn-secondary"
                    >
                      🔄 Regenerate All
                    </button>
                    {!guestMode && getSelectedMealsCount() > 0 && (
                      <button 
                        onClick={clearSelections}
                        className="text-sm text-gray-600 hover:text-gray-900 ml-2"
                      >
                        Clear Selections
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Daily Plan */}
              {activeTab === 'daily' && suggestions.meal_plan && (
                <div>
                  <div className="flex justify-between items-center mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
                    <h3 className="heading-4" style={{ color: 'rgb(24, 24, 24)' }}>Daily Meal Plan</h3>
                    <div className="flex items-center gap-2">
                      <label className="body-small whitespace-nowrap" style={{ color: 'rgb(24, 24, 24)' }}>Meal Date:</label>
                      <div className="relative">
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => setSelectedDate(date)}
                          className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ borderColor: 'rgba(24, 24, 24, 0.2)', minWidth: '160px' }}
                          dateFormat="MMM d, yyyy"
                          minDate={new Date()}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><span style={{ color: 'rgb(112, 112, 112)' }}>Total Calories:</span> <strong>{Math.round(suggestions.totals?.calories || 0)}</strong></div>
                      <div><span style={{ color: 'rgb(112, 112, 112)' }}>Protein:</span> <strong>{Math.round(suggestions.totals?.protein || 0)}g</strong></div>
                      <div><span style={{ color: 'rgb(112, 112, 112)' }}>Carbs:</span> <strong>{Math.round(suggestions.totals?.carbs || 0)}g</strong></div>
                      <div><span style={{ color: 'rgb(112, 112, 112)' }}>Fat:</span> <strong>{Math.round(suggestions.totals?.fat || 0)}g</strong></div>
                    </div>
                  </div>

                  <div className="space-y-6 mb-6">
                    {Object.entries(suggestions.meal_plan).map(([mealType, meals]) => (
                      meals.length > 0 && (
                        <div key={mealType}>
                          <h4 className="heading-4 mb-3 capitalize" style={{ color: 'rgb(24, 24, 24)' }}>
                            {mealType === 'breakfast' ? '🌅 Breakfast' : mealType === 'lunch' ? '🌞 Lunch' : mealType === 'dinner' ? '🌙 Dinner' : '🍪 Snack'}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {meals.map((meal, idx) => meal && (
                              <div key={idx} className="p-4 rounded-lg border relative group" style={{ 
                                borderColor: isMealSelected(meal.id, mealType) ? 'rgba(124, 58, 237, 0.5)' : 'rgba(24, 24, 24, 0.1)',
                                backgroundColor: isMealSelected(meal.id, mealType) ? 'rgba(124, 58, 237, 0.05)' : 'white',
                                paddingRight: '2.5rem' // Add padding to prevent text from going under the regenerate button
                              }}>
                                {/* Checkbox - Top right */}
                                <div className="absolute top-2 right-2 z-10">
                                  <input
                                    type="checkbox"
                                    checked={isMealSelected(meal.id, mealType)}
                                    onChange={() => toggleMealSelection(meal.id, mealType)}
                                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                
                                {/* Regenerate button - Top left */}
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    regenerateMeal(meal.id, mealType);
                                  }}
                                  className="absolute top-2 left-2 text-gray-400 hover:text-purple-600 p-1"
                                  title="Regenerate this meal"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </button>
                                
                                <div className="ml-6 flex-1">
                                  {editMode.isEditing && editMode.mealId === meal.id && editMode.mealType === mealType ? (
                                    <div className="mb-2">
                                      <SearchableDropdown
                                        value={{
                                          value: meal.id,
                                          label: `${meal.name} (${meal.calories} cal)`
                                        }}
                                        onChange={(selectedOption) => {
                                          if (selectedOption) {
                                            handleMealChange(mealType, meal.id, selectedOption.value);
                                          }
                                        }}
                                        options={[
                                          {
                                            value: meal.id,
                                            label: `${meal.name} (${meal.calories} cal) - Current`
                                          },
                                          ...(mealOptions[mealType] || [])
                                            .filter(m => m.id !== meal.id)
                                            .map(option => ({
                                              value: option.id,
                                              label: `${option.name} (${option.calories} cal, P:${option.protein}g C:${option.carbs}g F:${option.fat}g)`
                                            }))
                                        ]}
                                        isLoading={isLoadingOptions}
                                        placeholder="Search for a meal..."
                                        noOptionsMessage={({ inputValue }) => 
                                          inputValue ? 'No meals found. Try a different search.' : 'Start typing to search meals...'
                                        }
                                      />
                                      <div className="flex justify-end mt-1">
                                        <button
                                          onClick={() => setEditMode({ isEditing: false, mealId: null, mealType: null })}
                                          className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 hover:bg-gray-100 rounded"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-between">
                                      <h5 
                                        className="body-medium font-semibold mb-2 hover:text-purple-600 cursor-pointer" 
                                        style={{ color: 'rgb(24, 24, 24)' }}
                                        onClick={() => toggleEditMode(meal.id, mealType)}
                                      >
                                        {meal.name}
                                      </h5>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleEditMode(meal.id, mealType);
                                        }}
                                        className="text-gray-400 hover:text-purple-600 ml-2"
                                        title="Change meal"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                                  <div><span style={{ color: 'rgb(112, 112, 112)' }}>Cal:</span> {meal.calories}</div>
                                  <div><span style={{ color: 'rgb(112, 112, 112)' }}>P:</span> {meal.protein}g</div>
                                  <div><span style={{ color: 'rgb(112, 112, 112)' }}>C:</span> {meal.carbs}g</div>
                                  <div><span style={{ color: 'rgb(112, 112, 112)' }}>F:</span> {meal.fat}g</div>
                                </div>
                                <button 
                                  onClick={() => handleViewRecipe(meal)}
                                  className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                                >
                                  👩‍🍳 View Recipe
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                  </div>

                  {guestMode && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                      <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800">Sign in required to save meal plans</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          <button 
                            onClick={() => navigate('/login')}
                            className="underline hover:text-yellow-900 font-medium"
                          >
                            Create an account or sign in
                          </button> to save your meal plans and track your progress.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button 
                      onClick={guestMode ? () => navigate('/login') : () => {
                        const formattedDate = selectedDate.toISOString().split('T')[0];
                        savePlan(`Daily Plan - ${formattedDate}`);
                      }} 
                      className="btn-primary"
                      title={guestMode ? "Sign in to save" : ""}
                    >
                      {guestMode ? '🔒 Sign In to Save Plan' : '💾 Save Plan'}
                    </button>
                    <button onClick={() => fetchSuggestions('daily')} className="btn-secondary">🔄 Regenerate</button>
                  </div>
                </div>
              )}

              {/* Weekly Plan */}
              {activeTab === 'weekly' && suggestions.weekly_plan && (
                <div>
                  <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
                    <h3 className="heading-4 mb-2" style={{ color: 'rgb(24, 24, 24)' }}>📅 7-Day Meal Plan</h3>
                    <p className="body-small mb-2" style={{ color: 'rgb(112, 112, 112)' }}>
                      {suggestions.start_date} to {suggestions.end_date}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><span style={{ color: 'rgb(112, 112, 112)' }}>Weekly Calories:</span> <strong>{Math.round(suggestions.weekly_totals?.calories || 0)}</strong></div>
                      <div><span style={{ color: 'rgb(112, 112, 112)' }}>Protein:</span> <strong>{Math.round(suggestions.weekly_totals?.protein || 0)}g</strong></div>
                      <div><span style={{ color: 'rgb(112, 112, 112)' }}>Carbs:</span> <strong>{Math.round(suggestions.weekly_totals?.carbs || 0)}g</strong></div>
                      <div><span style={{ color: 'rgb(112, 112, 112)' }}>Fat:</span> <strong>{Math.round(suggestions.weekly_totals?.fat || 0)}g</strong></div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    {suggestions.weekly_plan.map((day, dayIdx) => (
                      <div key={dayIdx} className="p-4 rounded-lg border" style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}>
                        <h4 className="heading-4 mb-3" style={{ color: 'rgb(24, 24, 24)' }}>Day {dayIdx + 1} - {day.date}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3" style={{ color: 'rgb(112, 112, 112)' }}>
                          <div>Cal: <strong>{Math.round(day.totals?.calories || 0)}</strong></div>
                          <div>P: <strong>{Math.round(day.totals?.protein || 0)}g</strong></div>
                          <div>C: <strong>{Math.round(day.totals?.carbs || 0)}g</strong></div>
                          <div>F: <strong>{Math.round(day.totals?.fat || 0)}g</strong></div>
                        </div>
                        <details className="body-small">
                          <summary style={{ color: 'rgb(0, 128, 255)', cursor: 'pointer' }}>View Full Day Menu</summary>
                          <div className="mt-4 space-y-4">
                            {day.meal_plan && Object.entries(day.meal_plan).map(([mealType, meals]) => (
                              meals.length > 0 && (
                                <div key={mealType}>
                                  <p className="font-semibold capitalize mb-2" style={{ color: 'rgb(24, 24, 24)' }}>{mealType}:</p>
                                  <div className="space-y-2 ml-2">
                                    {meals.map((meal, idx) => meal && (
                                      <div key={idx} className="p-3 rounded border relative group" style={{ borderColor: 'rgba(24, 24, 24, 0.1)', paddingRight: '2.5rem' }}>
                                        {/* Regenerate button - Top left */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            regenerateMeal(meal.id, mealType, dayIdx);
                                          }}
                                          className="absolute top-2 left-2 text-gray-400 hover:text-purple-600 p-1"
                                          title="Regenerate this meal"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                          </svg>
                                        </button>
                                        <div className="ml-6">
                                          <div className="flex justify-between items-start">
                                            <div>
                                              <p style={{ color: 'rgb(24, 24, 24)' }}>{meal.name}</p>
                                              <p className="text-xs" style={{ color: 'rgb(112, 112, 112)' }}>
                                                {meal.calories} cal | P: {meal.protein}g | C: {meal.carbs}g | F: {meal.fat}g
                                              </p>
                                            </div>
                                            <button
                                              onClick={() => handleViewRecipe(meal)}
                                              className="text-xs text-purple-600 hover:text-purple-800 whitespace-nowrap ml-2"
                                            >
                                              (recipe)
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                            ))}
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>

                  {guestMode && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                      <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800">Sign in required to save meal plans</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          <button 
                            onClick={() => navigate('/login')}
                            className="underline hover:text-yellow-900 font-medium"
                          >
                            Create an account or sign in
                          </button> to save your meal plans and track your progress.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button 
                      onClick={guestMode ? () => navigate('/login') : () => savePlan(`Weekly Plan ${suggestions.start_date} to ${suggestions.end_date || ''}`)}
                      className="btn-primary"
                      title={guestMode ? "Sign in to save" : ""}
                    >
                      {guestMode ? '🔒 Sign In to Save' : '💾 Save Weekly Plan'}
                    </button>
                    <button onClick={() => fetchSuggestions('weekly')} className="btn-secondary">🔄 Regenerate</button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      
      {/* Recipe Modal */}
      <RecipeModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        recipe={selectedRecipe} 
      />
    </div>
  );
};

export default MealPlannerPage;